import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';
import { createNewFolder, userEnteringNewFolderName } from '../actions/internalActions';
import {
    getNewFolderName,
    isCreatingNewFolder,
    isUserEnteringNewFolderName,
} from '../selectors/createNewFolderSelectors';
import { getOwaCommonStrings } from 'owa-strings/owa-common-strings';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { observer } from 'mobx-react';
import { SelectableTextField } from './SelectableTextField';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';

const styles = require('./NewFolderField.scss');

@observer
export class NewFolderField extends React.Component<{}, {}> {
    private textField = React.createRef<SelectableTextField>();
    private container = React.createRef<HTMLDivElement>();

    render() {
        const creatingNewFolder = isCreatingNewFolder();
        return (
            <div
                className={styles.container}
                ref={this.container}
                onKeyDown={this.onKeyDown}
                onClick={this.focusOnTextField}
                data-is-focusable={true}>
                {creatingNewFolder
                    ? this.renderCreatingNewFolder()
                    : this.renderIconWithSelectableTextField()}
            </div>
        );
    }

    public focus() {
        this.focusOnTextField();
    }

    private renderCreatingNewFolder() {
        const folderName = getNewFolderName();
        return (
            <>
                <Spinner className={styles.icon} size={SpinnerSize.small} />
                <div className={styles.textFieldContainer} title={folderName}>
                    {folderName}
                </div>
            </>
        );
    }

    private renderIconWithSelectableTextField() {
        const strings = getOwaCommonStrings();
        const iconName: ControlIcons = isUserEnteringNewFolderName()
            ? ControlIcons.FabricFolder
            : ControlIcons.Add;
        return (
            <>
                <Icon className={styles.icon} iconName={iconName} />
                <SelectableTextField
                    ref={this.textField}
                    className={styles.textFieldContainer}
                    textFieldClassName={styles.textField}
                    placeholderText={strings.newFolderPlaceholderText}
                    placeholderTextFieldValue={strings.enterFolderNamePlaceholderText}
                    onEntry={this.onTextFieldValueEntered}
                    onDismiss={this.onTextFieldDismissed}
                    onUserPressEscape={this.onUserPressEscapeInTextField}
                />
            </>
        );
    }

    private focusOnTextField = () => {
        if (this.textField.current) {
            userEnteringNewFolderName(true);
            this.textField.current.focus();
        }
    };

    private focusOnContainer() {
        if (this.container.current) {
            this.container.current.focus();
        }
    }

    private onKeyDown = (evt: React.KeyboardEvent<EventTarget>) => {
        switch (evt.which) {
            case KeyCodes.enter:
                // On enter focus on the text field
                this.focusOnTextField();
                evt.stopPropagation();
                break;
        }
    };

    private onUserPressEscapeInTextField = () => {
        // We only return focus back to the container
        // and stop propogation of event if the user
        // is in the new folder entering mode and presses
        // escape
        this.focusOnContainer();
    };

    private onTextFieldValueEntered = (folderName: string) => {
        // When user enters text we need to shift the focus back to the row
        this.focusOnContainer();

        if (folderName.trim() !== '') {
            createNewFolder(folderName);
        }
    };

    private onTextFieldDismissed = () => {
        userEnteringNewFolderName(false);
    };
}
